/**
 * Offering the catalogue to the pattern workbench. Task 5.2, requirements 21.2,
 * 21.4, 21.5, 2.2, 2.3 and 24.1.
 *
 * Three tools, registered through the raw path because `exposedTo` names the origin
 * allowed to see them and the hook has nowhere to put that. The descriptors are not
 * written here: they come from `@jig/tools`, which the workbench reads as well, so
 * the names, the schemas and the two annotation hints are one set of objects rather
 * than two that agree today. All three carry the `yarns_` prefix, say in their
 * descriptions that they are a data source meant to be used through the broker
 * verbs, and carry `readOnlyHint` true with `untrustedContentHint` true.
 *
 * ### The origin comes from configuration
 *
 * `exposedTo` is `[origins.jig]`, and `origins` is resolved from `VITE_JIG_ORIGIN`
 * by `@jig/origins`. Nothing here spells a hostname. The two sides have to agree
 * character for character: the workbench asks for tools with `fromOrigins` naming
 * this origin and we answer with `exposedTo` naming its, and a single character of
 * disagreement produces an empty tool list rather than an error anybody can see.
 *
 * ### Registration reports, it does not throw
 *
 * This page runs inside an iframe on the workbench, and a frame without
 * `allow="tools"` refuses registration with `NotAllowedError`. That is a state of
 * the deployment, not a bug in this file, so `registerToolRaw` hands back a typed
 * outcome per tool and the page prints which of the three took and why the others
 * did not. Nothing below throws and nothing below needs a try block around it.
 *
 * ### The host is a parameter
 *
 * {@link pageModelContext} is the only function in this app that looks at
 * `document.modelContext`. Everything else takes the context it is given, which is
 * why the tests here register against a plain object instead of a browser.
 *
 * ### The answers carry untrusted text on purpose
 *
 * Yarn names, fibre descriptions and community notes are strings this origin
 * accepted from outside, and they go out as they came in. That includes the seeded
 * injection note in `catalogue.ts`. This service does not scan, redact, flag or
 * soften anything, and a later reader should not add filtering here "to be safe":
 * the workbench quarantines what crosses into it at its own boundary, and a source
 * that sanitised its own output would be asking to be trusted, which is exactly the
 * arrangement `untrustedContentHint` exists to refuse. An attacker with an account
 * on this site would route around any check made here anyway.
 */

import type { Origins } from '@jig/origins';
import {
  MAX_OUTPUT_CHARS,
  yarnsFindYarn,
  yarnsSubstitutionCandidates,
  yarnsYarnProfile,
  type ToolText,
} from '@jig/tools';
import {
  asModelContext,
  errorToolText,
  registerToolRaw,
  type ModelContextLike,
  type RegistrationOutcome,
} from '@jig/webmcp';

import {
  findYarns,
  notesFor,
  substitutionCandidates,
  yarnById,
  YARNS,
  type CommunityNote,
  type YarnRecord,
} from './catalogue';

// ---------- the one look at the host ----------

/** Reading a global by name, because a bare identifier would be a ReferenceError. */
const globalValue = (name: string): unknown => {
  try {
    return (globalThis as Record<string, unknown>)[name];
  } catch {
    return undefined;
  }
};

/**
 * This page's model context, or null on a browser without the surface.
 *
 * The whole app's feature detection, in five lines and one place. The workbench
 * keeps a `platform` directory for the same purpose with a lint rule behind it;
 * this service has one page, so one function is enough, but the discipline is the
 * same and the reason is the same: a registration path that reaches for `document`
 * itself cannot be tested without a browser.
 */
export const pageModelContext = (): ModelContextLike | null => {
  const documentLike = globalValue('document');
  if (typeof documentLike !== 'object' || documentLike === null) return null;
  try {
    return asModelContext((documentLike as Record<string, unknown>)['modelContext']);
  } catch {
    // A draft API can be a getter that raises. A page that cannot boot because of
    // one is worse than a page with no tools.
    return null;
  }
};

// ---------- what the answers look like on the wire ----------

/**
 * A tool result is text, per requirement 11.7, so the payload travels as JSON
 * inside it. These four types are that contract written down. The workbench states
 * the same shapes from its side in `yarnSource.ts`, and the two were checked
 * against each other field by field when this file was written: `matches` entries
 * carry `yarnId` and `name`, a profile carries both gauge figures, the yards per
 * skein and the origin it came from, and every number is a number rather than a
 * string, because the reader on the other side refuses a payload where one is
 * missing instead of guessing at it.
 */
export interface NoteWire {
  readonly id: string;
  readonly author: string;
  readonly postedOn: string;
  readonly body: string;
}

/** One search hit. Enough to choose by, which is why it holds no figures. */
export interface MatchWire {
  readonly yarnId: string;
  readonly name: string;
  readonly weight: string;
}

export interface SearchPayload {
  readonly matches: ReadonlyArray<MatchWire>;
  /**
   * A sample of what is in stock, sent only when nothing matched.
   *
   * A caller told its query failed and nothing else can only guess again. When
   * there are matches this is left out rather than sent empty, because every
   * character of it would be a character taken off the matches.
   */
  readonly offered?: ReadonlyArray<string>;
  readonly remainder?: string;
}

export interface ProfilePayload {
  readonly yarnId: string;
  readonly name: string;
  readonly weight: string;
  readonly stsPer4in: number;
  readonly rowsPer4in: number;
  readonly minStsPer4in: number;
  readonly maxStsPer4in: number;
  readonly yardsPerSkein: number;
  readonly fibre: string;
  readonly sourceOrigin: string;
}

/**
 * One substitute.
 *
 * No fibre content, unlike a profile. This tool promises gauge ranges, yards per
 * skein and notes, and a substitution is decided on the figures, so the fibre is
 * left to `yarns_yarn_profile` where there is room for it.
 */
export interface CandidateWire {
  readonly yarnId: string;
  readonly name: string;
  readonly weight: string;
  readonly stsPer4in: number;
  readonly rowsPer4in: number;
  readonly minStsPer4in: number;
  readonly maxStsPer4in: number;
  readonly yardsPerSkein: number;
  readonly notes?: ReadonlyArray<NoteWire>;
  /** Set where notes exist but did not fit, so absent notes are not read as none. */
  readonly notesHeld?: true;
}

export interface CandidatesPayload {
  readonly yarnId: string;
  readonly sourceOrigin: string;
  /**
   * The notes on the yarn that was asked about, not on its substitutes.
   *
   * Here for two reasons. Whether to leave a yarn is decided partly by what people
   * say about the one you are on, and it makes the route to a dropped candidate's
   * notes exact: ask this tool about that candidate and its own notes come back
   * here.
   */
  readonly notes?: ReadonlyArray<NoteWire>;
  readonly candidates: ReadonlyArray<CandidateWire>;
  readonly remainder?: string;
}

// ---------- the output budget ----------

/**
 * Room kept back for the sentence that names the route to whatever did not fit.
 *
 * Requirement 11.3 holds a result to 1500 characters, and the registration wrapper
 * enforces that by truncating. Truncated JSON does not parse, so nothing here is
 * allowed to reach the wrapper's cut: each payload is assembled against the budget
 * and stops while it is still whole. The wrapper measures the sum of the part
 * lengths rather than the joined length, which is the same number here because
 * every answer is a single text part.
 */
const REMAINDER_ROOM = 240;

const BUILD_BUDGET = MAX_OUTPUT_CHARS - REMAINDER_ROOM;

const sizeOf = (payload: unknown): number => JSON.stringify(payload).length;

const asText = (payload: unknown): string => JSON.stringify(payload);

/** How many names a failed search reports back. */
const OFFERED_LIMIT = 6;

// ---------- reading what the caller sent ----------

/**
 * The arguments, whether they arrived as an object or as a JSON string.
 *
 * Which of the two a build sends is still unsettled, and the workbench records the
 * answer at runtime rather than assuming it. A data source is the wrong place to
 * have an opinion about it, so both are read.
 */
const asRecord = (input: unknown): Record<string, unknown> | null => {
  let value: unknown = input;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
};

const textField = (raw: Record<string, unknown>, key: string): string | null => {
  const value = raw[key];
  return typeof value === 'string' ? value : null;
};

const numberField = (raw: Record<string, unknown>, key: string): number | null => {
  const value = raw[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const WEIGHT_WORDS = ['fingering', 'sport', 'dk', 'worsted', 'aran'] as const;

const stockedIds = (): string => YARNS.map((yarn) => yarn.id).join(', ');

// ---------- turning catalogue rows into payload entries ----------

const noteWire = (note: CommunityNote): NoteWire => ({
  id: note.id,
  author: note.author,
  postedOn: note.postedOn,
  // As submitted. See the note at the top of this file about why nothing is done
  // to it on the way out.
  body: note.body,
});

const matchWire = (yarn: YarnRecord): MatchWire => ({
  yarnId: yarn.id,
  name: yarn.name,
  weight: yarn.weight,
});

const profilePayload = (yarn: YarnRecord, origins: Origins): ProfilePayload => ({
  yarnId: yarn.id,
  name: yarn.name,
  weight: yarn.weight,
  // The swatch pair, which is the gauge a pattern can be recomputed at. The ball
  // band range travels beside it rather than instead of it, because a range cannot
  // be knitted at and a consumer that had only the range would have to invent a row
  // figure to use it.
  stsPer4in: yarn.stsPer4in,
  rowsPer4in: yarn.rowsPer4in,
  minStsPer4in: yarn.minStsPer4in,
  maxStsPer4in: yarn.maxStsPer4in,
  yardsPerSkein: yarn.yardsPerSkein,
  fibre: yarn.fibre,
  // Stated rather than inferred, because it is what the consumer keys its own
  // quarantine on. Configuration, not a literal.
  sourceOrigin: origins.yarns,
});

const candidateWire = (yarn: YarnRecord, notes: ReadonlyArray<NoteWire>): CandidateWire => ({
  yarnId: yarn.id,
  name: yarn.name,
  weight: yarn.weight,
  stsPer4in: yarn.stsPer4in,
  rowsPer4in: yarn.rowsPer4in,
  minStsPer4in: yarn.minStsPer4in,
  maxStsPer4in: yarn.maxStsPer4in,
  yardsPerSkein: yarn.yardsPerSkein,
  ...(notes.length === 0 ? {} : { notes }),
});

// ---------- the three answers ----------

/**
 * The search, filled up to the budget.
 *
 * Matches go in in the catalogue's own order until the next one would not fit, and
 * anything left out is named as a narrower query, which is a real route: the same
 * search with another word or the weight filter set reaches it.
 *
 * `budget` is a parameter with the requirement's own figure as its default. A shop
 * this size cannot fill 1500 characters with names, so the branch that drops a match
 * is only reachable from a test that says how much room there is.
 */
export const searchPayload = (
  query: string,
  weight: string | undefined,
  budget: number = BUILD_BUDGET,
): SearchPayload => {
  const found = findYarns(query, weight);

  const matches: Array<MatchWire> = [];
  let unlisted = 0;
  for (const yarn of found) {
    if (sizeOf({ matches: [...matches, matchWire(yarn)] }) > budget) {
      unlisted = found.length - matches.length;
      break;
    }
    matches.push(matchWire(yarn));
  }

  const offered =
    matches.length === 0
      ? YARNS.slice(0, OFFERED_LIMIT).map((yarn) => yarn.name)
      : undefined;

  return {
    matches,
    ...(offered === undefined ? {} : { offered }),
    ...(unlisted === 0
      ? {}
      : {
          remainder: `${unlisted} more ${unlisted === 1 ? 'yarn matches' : 'yarns match'} and did not fit this result. Call yarns_find_yarn with another word in the query, or with weight set, to reach them.`,
        }),
  };
};

/**
 * The substitutes, filled up to the budget, notes and all.
 *
 * A note is the largest thing this service sends and the reason this payload needs
 * assembling rather than mapping: one long note can be worth four candidates. So
 * each candidate is tried with its notes, then without them, and the first one that
 * does not fit either way ends the list, since every entry after it is much the same
 * size. What was held back is named along with the call that returns it, which for a
 * note is this same tool asked about that yarn.
 *
 * `budget` is the room available, defaulted from the requirement and passed in by the
 * tests so the fill rule can be watched at sizes the shop cannot produce.
 */
export const candidatesPayload = (
  yarn: YarnRecord,
  limit: number | undefined,
  origins: Origins,
  budget: number = BUILD_BUDGET,
): CandidatesPayload => {
  // The subject's notes go in first, and they are measured too. A note is text
  // somebody else wrote and there is no length this service can promise about it, so
  // the one that does not fit is left out whole rather than cut: half a sentence of
  // an untrusted note is worse than a stated absence.
  const ownNotes: Array<NoteWire> = [];
  let ownNotesHeld = 0;
  for (const note of notesFor(yarn.id).map(noteWire)) {
    const draft = { yarnId: yarn.id, sourceOrigin: origins.yarns, notes: [...ownNotes, note] };
    if (sizeOf(draft) <= budget) ownNotes.push(note);
    else ownNotesHeld += 1;
  }

  const frame = {
    yarnId: yarn.id,
    sourceOrigin: origins.yarns,
    ...(ownNotes.length === 0 ? {} : { notes: ownNotes }),
  };

  const ranked = substitutionCandidates(yarn.id, limit);

  const candidates: Array<CandidateWire> = [];
  const notesHeldFor: Array<string> = [];
  const leftOut: Array<string> = [];

  for (const candidate of ranked) {
    if (leftOut.length > 0) {
      leftOut.push(candidate.id);
      continue;
    }

    const notes = notesFor(candidate.id).map(noteWire);
    const full = candidateWire(candidate, notes);
    if (sizeOf({ ...frame, candidates: [...candidates, full] }) <= budget) {
      candidates.push(full);
      continue;
    }

    const bare: CandidateWire = { ...candidateWire(candidate, []), notesHeld: true };
    if (notes.length > 0 && sizeOf({ ...frame, candidates: [...candidates, bare] }) <= budget) {
      candidates.push(bare);
      notesHeldFor.push(candidate.id);
      continue;
    }

    leftOut.push(candidate.id);
  }

  const compose = (): CandidatesPayload => {
    const sentences: Array<string> = [];
    if (ownNotesHeld > 0) {
      sentences.push(
        `${ownNotesHeld} ${ownNotesHeld === 1 ? 'note' : 'notes'} on this yarn did not fit. Call yarns_substitution_candidates with limit set to 1 to leave room for them.`,
      );
    }
    if (notesHeldFor.length > 0) {
      sentences.push(
        `The notes on ${notesHeldFor.join(', ')} did not fit. Call yarns_substitution_candidates with yarnId set to that yarn to read them.`,
      );
    }
    if (leftOut.length > 0) {
      sentences.push(
        `Also substituting: ${leftOut.join(', ')}. Call yarns_yarn_profile with one of those ids for its figures, or this tool for its notes.`,
      );
    }
    return {
      ...frame,
      candidates: [...candidates],
      ...(sentences.length === 0 ? {} : { remainder: sentences.join(' ') }),
    };
  };

  // The sentence naming what was held back is itself part of the result, and it
  // grows as more is held back, so the budget above cannot be the last word. Trim
  // from the end until the whole thing fits, which is at most a couple of turns
  // because each dropped entry buys more than the id it adds to the sentence.
  let payload = compose();
  while (sizeOf(payload) > MAX_OUTPUT_CHARS && candidates.length > 0) {
    const dropped = candidates.pop();
    if (dropped === undefined) break;
    const heldAt = notesHeldFor.indexOf(dropped.yarnId);
    if (heldAt >= 0) notesHeldFor.splice(heldAt, 1);
    leftOut.unshift(dropped.yarnId);
    payload = compose();
  }

  return payload;
};

// ---------- the executes ----------

/**
 * A refusal an agent can act on: the parameter, what arrived, what is accepted.
 *
 * An `isError` result rather than a throw, which the wrapper would convert anyway.
 * Doing it here means the sentence names the parameter instead of the exception.
 */
const refuse = (message: string): ToolText => errorToolText(message);

export const findYarnTool = async (input: unknown): Promise<string | ToolText> => {
  const raw = asRecord(input);
  if (raw === null) {
    return refuse('yarns_find_yarn takes an object with a query, given as text.');
  }

  const query = textField(raw, 'query');
  if (query === null) {
    const received = raw['query'] === undefined ? 'nothing' : JSON.stringify(raw['query']);
    return refuse(
      `yarns_find_yarn needs query as text, for example "worsted wool". Received ${received}.`,
    );
  }

  const weight = textField(raw, 'weight');
  const wanted = weight === null ? undefined : weight.trim().toLowerCase();
  if (wanted !== undefined && wanted !== '' && !WEIGHT_WORDS.some((word) => word === wanted)) {
    return refuse(
      `yarns_find_yarn stocks ${WEIGHT_WORDS.join(', ')}, and weight was ${JSON.stringify(weight)}. Leave weight out to search every weight.`,
    );
  }

  return asText(searchPayload(query, wanted === '' ? undefined : wanted));
};

export const yarnProfileTool =
  (origins: Origins) =>
  async (input: unknown): Promise<string | ToolText> => {
    const raw = asRecord(input);
    const yarnId = raw === null ? null : textField(raw, 'yarnId');
    if (yarnId === null) {
      return refuse(
        'yarns_yarn_profile needs yarnId as text, taken from a yarns_find_yarn result.',
      );
    }

    const yarn = yarnById(yarnId.trim());
    if (yarn === undefined) {
      return refuse(
        `This catalogue holds no yarn under the id ${JSON.stringify(yarnId)}. The ids in stock are ${stockedIds()}.`,
      );
    }

    return asText(profilePayload(yarn, origins));
  };

export const substitutionCandidatesTool =
  (origins: Origins) =>
  async (input: unknown): Promise<string | ToolText> => {
    const raw = asRecord(input);
    const yarnId = raw === null ? null : textField(raw, 'yarnId');
    if (yarnId === null) {
      return refuse(
        'yarns_substitution_candidates needs yarnId as text, taken from a yarns_find_yarn result.',
      );
    }

    const yarn = yarnById(yarnId.trim());
    if (yarn === undefined) {
      return refuse(
        `This catalogue holds no yarn under the id ${JSON.stringify(yarnId)}, so there is nothing to find substitutes for. The ids in stock are ${stockedIds()}.`,
      );
    }

    // A limit the catalogue cannot honour is clamped there rather than refused
    // here, since a caller asking for twenty of seven wants the seven.
    const limit = raw === null ? null : numberField(raw, 'limit');
    return asText(candidatesPayload(yarn, limit === null ? undefined : limit, origins));
  };

// ---------- registration ----------

/** One outcome per tool, in the order the three were attempted. */
export type YarnToolOutcomes = ReadonlyArray<RegistrationOutcome>;

/**
 * The three names, in the order {@link registerYarnTools} attempts them.
 *
 * Read off the descriptors rather than written out, so the page cannot name a tool
 * the catalogue does not register.
 */
export const yarnToolNames = (origins: Origins): ReadonlyArray<string> => [
  yarnsFindYarn(origins).name,
  yarnsYarnProfile(origins).name,
  yarnsSubstitutionCandidates(origins).name,
];

export interface RegisterOptions {
  /** Aborting this takes all three registrations away again. */
  readonly signal: AbortSignal;
  /**
   * Abort the previous attempt at these names and resolve once it has settled.
   *
   * Only ever called on a duplicate name, which on this page means a remount:
   * React's strict mode runs an effect twice in development, and the second run
   * can reach a name the first has not finished releasing.
   */
  readonly retirePrevious?: (() => void | PromiseLike<void>) | undefined;
}

/**
 * Offer all three tools to the host, and report what each one did.
 *
 * Sequential rather than in parallel so the outcomes come back in catalogue order
 * and a duplicate name retry cannot race its own neighbours. Three awaited calls
 * on a page load is not a cost worth optimising.
 *
 * `exposedTo` is not passed here. It is on the descriptors, resolved from the
 * origin configuration by whoever built them, and the wrapper reads it off the spec.
 * One source for it means the workbench and this service cannot drift apart.
 */
export async function registerYarnTools(
  context: ModelContextLike | null,
  origins: Origins,
  opts: RegisterOptions,
): Promise<YarnToolOutcomes> {
  const raw = {
    signal: opts.signal,
    ...(opts.retirePrevious === undefined ? {} : { retirePrevious: opts.retirePrevious }),
  };

  const attempts = [
    (): Promise<RegistrationOutcome> =>
      registerToolRaw(context, yarnsFindYarn(origins), findYarnTool, raw),
    (): Promise<RegistrationOutcome> =>
      registerToolRaw(context, yarnsYarnProfile(origins), yarnProfileTool(origins), raw),
    (): Promise<RegistrationOutcome> =>
      registerToolRaw(
        context,
        yarnsSubstitutionCandidates(origins),
        substitutionCandidatesTool(origins),
        raw,
      ),
  ];

  const outcomes: Array<RegistrationOutcome> = [];
  for (const attempt of attempts) {
    outcomes.push(await attempt());
  }
  return outcomes;
}

/**
 * Take all three registrations away, and resolve once the names are free.
 *
 * The abort is the contract: requirement 15.2 makes the signal handed over at
 * registration the way a tool is withdrawn, and `registerToolRaw` passes that signal
 * as the second argument to `registerTool`, which is the position Chrome Canary
 * 154.0.8032.0 reads it from. The explicit calls after it are for a build with an
 * `unregisterTool`, which that Chrome does not have at all, so they are a courtesy
 * rather than a fallback. A host that objects to being called is not a reason for a
 * teardown to fail either way.
 */
export async function retireYarnTools(
  context: ModelContextLike | null,
  origins: Origins,
  controller: AbortController,
): Promise<void> {
  controller.abort();
  const unregister = context?.unregisterTool;
  if (typeof unregister !== 'function') return;
  for (const name of yarnToolNames(origins)) {
    try {
      await unregister.call(context, name);
    } catch {
      // Nothing to do about it, and nothing worth reporting: the abort above is
      // the route that matters.
    }
  }
}
